/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import StoryStrip from './StoryStrip';
import InstallsTable from './InstallsTable';
import { SearchDialog, FiltersDialog, FlagDialog, EventsDialog, AboutDialog } from './InstallsDialogs';
import {
  DEFAULT_FILTERS, DEFAULT_SORT, SORT_FIELDS, SYNCED_BUILDS, PRESETS, buildLabel, describe, dialogFilterCount,
  normaliseFilters, optionCounts, parseHash, presetDates, serializeHash, sortRows, story as buildStory, visibleRows,
} from './installsModel';
import { browserZone, buildCsv, csvFilename, rangeLabel, shortDay } from './installsFormat';
import '../active-users/active-users.css';
import './installs.css';

/**
 * Installs (099) — /installs
 *
 * Every first open in a range, and whether that installation went on to open the app, send our
 * install event, start and finish a sign-in within 48 hours. Spec: INSTALLS_PAGE_PORTAL_SPEC.md.
 *
 * ONE request per search (GET /admin/api/installs/cohort), over the portal's own copy of the GA4
 * export. After that nothing re-queries: filters, the story, sort, text search and the exports
 * all run over the rows in the browser. The whole view lives in the URL hash so a link can be
 * shared; the hash is updated with replaceState, because assigning it fires `hashchange`, which
 * would re-run the search on every filter click.
 */

/**
 * Where the page's answer comes from. `local` (099, /installs) asks the portal's synced copy;
 * `bigquery` (112, /installs-bigquery — the View button on Installation Statistics) asks the GA4
 * export itself, so a search costs a BigQuery scan and is held to 3 days.
 */
const SOURCES = {
  local: {
    endpoint: '/admin/api/installs/cohort',
    title: 'Installs',
    route: '/installs',
    crumb: 'Growth & engagement',
    back: '/dashboard',
    maxDays: 31,
    hint: 'At most 31 days. Only days synced on the BigQuery Events screen have data.',
    missing: (n) => `${n} ${n === 1 ? 'day is' : 'days are'} not synced`,
  },
  bigquery: {
    endpoint: '/admin/api/installs/bigquery-cohort',
    title: 'Installs · BigQuery',
    route: '/installs-bigquery',
    crumb: 'Installations',
    back: '/installation-data-statics',
    maxDays: 3,
    hint: 'At most 3 days — every search is a BigQuery query. Today is read while it is still streaming.',
    missing: (n) => `${n} ${n === 1 ? 'day has' : 'days have'} no BigQuery data`,
  },
};

const BUILD_KEY = 'installsBuild';
const EMPTY_SEARCH = { from: '', to: '', build: '', versions: '', device: '', rows: '' };

const readBuild = () => {
  try {
    const v = localStorage.getItem(BUILD_KEY) || '';
    return SYNCED_BUILDS.some((b) => b.key === v) ? v : '';
  } catch {
    return '';
  }
};
const writeBuild = (v) => {
  try { localStorage.setItem(BUILD_KEY, v); } catch { /* storage blocked — the page still works */ }
};

const Installs = ({ now = Date.now, source = 'local' }) => {
  const cfg = SOURCES[source] || SOURCES.local;
  const navigate = useNavigate();
  const zone = useMemo(browserZone, []);
  const initial = useMemo(() => parseHash(typeof window !== 'undefined' ? window.location.hash : ''), []);

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [search, setSearch] = useState(initial.search);
  const [shownSearch, setShownSearch] = useState(null); // the search the list on screen answers
  const [phase, setPhase] = useState('idle');
  const [data, setData] = useState({ rows: [], orphans: [], meta: null });
  const [errorMsg, setErrorMsg] = useState('');
  const [filters, setFilters] = useState(initial.filters);
  const [sort, setSort] = useState(initial.sort);
  const [expanded, setExpanded] = useState(() => new Set());
  const [dialog, setDialog] = useState(initial.search ? null : 'search');
  const [flag, setFlag] = useState(null);
  const [toast, setToast] = useState('');
  const [focusChip, setFocusChip] = useState(null);
  const inFlight = useRef(false);
  const chipsRef = useRef(null);
  const filtersBtn = useRef(null);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => { refreshToken(); }, []);

  // A hash with `from` runs the search as soon as the token is in.
  useEffect(() => { if (token && initial.search && phase === 'idle') run(initial.search); }, [token]);

  // Keep the URL in step with what is on screen — replaceState, never an assignment.
  useEffect(() => {
    try {
      const hash = serializeHash({ search: shownSearch || search, filters, sort });
      const url = `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ''}`;
      window.history.replaceState(window.history.state, '', url);
    } catch { /* a sandboxed frame may refuse; the page still works */ }
  }, [search, shownSearch, filters, sort]);

  // A hand-edited or pasted hash: re-read it, and search again only when the search changed.
  useEffect(() => {
    const onHash = () => {
      const next = parseHash(window.location.hash);
      setFilters(next.filters);
      setSort(next.sort);
      if (next.search && serializeHash({ search: next.search, filters: DEFAULT_FILTERS, sort: DEFAULT_SORT })
        !== serializeHash({ search: shownSearch, filters: DEFAULT_FILTERS, sort: DEFAULT_SORT })) {
        run(next.search);
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [shownSearch, token]);

  const flash = (text) => { setToast(text); setTimeout(() => setToast(''), 1800); };
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      flash('Copied');
    } catch {
      flash('Could not copy — the browser refused');
    }
  };

  /** The page's only request. A second submit while one is in flight is ignored. */
  async function run(s) {
    if (inFlight.current || !s) return;
    inFlight.current = true;
    setSearch(s);
    setShownSearch(s);
    setPhase('loading');
    setErrorMsg('');
    setDialog(null);
    try {
      const qs = new URLSearchParams({ from_date: s.from, to_date: s.to || s.from });
      if (s.build) qs.set('target', s.build);
      if (s.versions) qs.set('versions', s.versions);
      if (s.device) qs.set('device_id', s.device);
      if (s.rows) qs.set('row_limit', s.rows);
      const res = await axiosJWT.get(`${baseUrl}${cfg.endpoint}?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data?.data || { rows: [], orphans: [], meta: null };
      setData(d);
      setExpanded(new Set());
      setPhase(d.rows.length || d.orphans.length ? 'results' : 'empty');
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/'); return; }
      setErrorMsg(err?.response?.data?.message || 'Could not load the installs. Try again later.');
      setPhase('error');
    } finally {
      inFlight.current = false;
    }
  }

  const submitSearch = (s) => {
    writeBuild(s.build);
    run(s);
  };

  const openSearch = () => setDialog('search');
  const defaultSearch = () => ({ ...EMPTY_SEARCH, ...presetDates(PRESETS[2], now()), build: readBuild() });

  const patchFilters = (patch) => setFilters((f) => normaliseFilters({ ...f, ...patch }));

  // Derived views — all over the rows already loaded.
  const rows = data.rows || [];
  const orphans = data.orphans || [];
  const theStory = useMemo(() => buildStory(rows, filters), [rows, filters]);
  const visible = useMemo(() => sortRows(visibleRows(rows, filters), sort.field, sort.dir), [rows, filters, sort]);
  const sortedOrphans = useMemo(() => sortRows(orphans, sort.field, sort.dir), [orphans, sort]);
  const counts = useMemo(() => optionCounts(rows, filters), [rows, filters]);
  const chips = describe(filters).filter((c) => c.key !== 'text');
  const anyApplied = describe(filters).length > 0;
  const versionsSeen = useMemo(() => new Set([...rows, ...orphans].map((r) => r.appVersion).filter(Boolean)), [rows, orphans]);

  // After removing a chip, focus the next one — or the Filters button when none remain.
  useEffect(() => {
    if (focusChip === null) return;
    const list = chipsRef.current ? chipsRef.current.querySelectorAll('[data-fchip] button') : [];
    (list[Math.min(focusChip, list.length - 1)] || filtersBtn.current)?.focus();
    setFocusChip(null);
  }, [focusChip]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const onSort = (field) => setSort((s) => (s.field === field ? { field, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { field, dir: 'desc' }));

  const exportCsv = () => {
    const blob = new Blob([buildCsv(visible)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = csvFilename(data.meta, filters);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const bar = shownSearch || search;
  const meta = data.meta;
  const filteredBy = bar ? [
    bar.build && `Build = ${buildLabel(bar.build) || bar.build}`,
    bar.versions && `Version = ${bar.versions}`,
    bar.device && `Device = ${bar.device}`,
  ].filter(Boolean) : [];

  const queryBar = bar && (
    <div className="inst-qbar" data-testid="inst-qbar">
      <span className="inst-chip inst-chip--strong">{bar.build ? (buildLabel(bar.build) || bar.build) : 'All builds'}</span>
      <span className="inst-chip">{rangeLabel(bar.from, bar.to || bar.from)}</span>
      {bar.versions && <span className="inst-chip">v {bar.versions}</span>}
      {bar.device && <span className="inst-chip">device {bar.device}</span>}
      {bar.rows && <span className="inst-chip">cap {bar.rows}</span>}
      <span className="inst-qmeta">
        {meta && phase === 'results' && (
          <>
            answers read to {shortDay(meta.followUpTo)}
            {meta.intradayDays > 0 && ' (still streaming)'}
            {' · '}{meta.rowCount} rows · {meta.queryMillis} ms
            {zone !== 'UTC' && ` · UTC + ${zone}`}
          </>
        )}
        {meta && meta.daysMissing > 0 && (
          <span className="inst-warn-text">
            {phase === 'results' ? ' · ' : ''}
            {cfg.missing(meta.daysMissing)} ({meta.missingDays.map(shortDay).join(', ')})
          </span>
        )}
      </span>
      <button type="button" className="inst-btn inst-btn--primary" onClick={openSearch}>Edit search</button>
      <button type="button" className="inst-btn" onClick={() => setDialog('events')}>Events</button>
      <button type="button" className="inst-btn inst-btn--round" aria-label="About this page" onClick={() => setDialog('about')}>i</button>
    </div>
  );

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">{cfg.crumb} › <b>{cfg.title}</b></span>
            <span className="au-route">{cfg.route}</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to={cfg.back}>← Back</Link></span>
          </div>
          <h1 className="au-title" style={{ marginBottom: 10 }}>{cfg.title}</h1>

          <div className="inst" data-testid="installs-page">
            {queryBar}

            {source === 'bigquery' && (
              <div className="inst-note" role="note" data-testid="inst-source-note">
                <span className="inst-note-i" aria-hidden="true">i</span>
                <span>
                  Every count on this page comes <b>straight from BigQuery</b> (the GA4 export), not the portal&apos;s synced copy.
                  Today&apos;s events are still streaming, so today&apos;s answers can still change.
                </span>
              </div>
            )}

            <div className="inst-note" role="note">
              <span className="inst-note-i" aria-hidden="true">i</span>
              <span>
                Each answer counts events within <b>48 hours of that install&apos;s first open</b>. Installs less than
                48 hours old are still inside the window, so their answers show as <span className="inst-pill inst-pill--pending">pending</span> and can still change.
              </span>
            </div>

            {phase === 'idle' && (
              <div className="inst-state">
                <p style={{ margin: '0 0 12px' }}>Every first open in a range, and whether that install went on to open the app, send our install event, and sign in.</p>
                <button type="button" className="inst-btn inst-btn--primary" onClick={openSearch}>Search installs</button>
              </div>
            )}
            {phase === 'loading' && (
              <div className="inst-state" aria-live="polite"><div className="inst-spinner" />Running the query…</div>
            )}
            {phase === 'error' && <div className="inst-state inst-state--error" role="alert">{errorMsg}</div>}
            {phase === 'empty' && (
              <div className="inst-state">
                <strong>No installs.</strong>{' '}
                {meta?.note || (
                  <>No first_open between {bar.from} and {bar.to || bar.from}{filteredBy.length ? `, filtered by ${filteredBy.join(' · ')}.` : '.'}</>
                )}
              </div>
            )}

            {phase === 'results' && (
              <>
                <StoryStrip story={theStory} focus={filters.focus} onFocus={(focus) => patchFilters({ focus })} />

                <div className="inst-showing">
                  <div className="inst-showing-left" ref={chipsRef}>
                    <span data-testid="inst-showing">Showing <b>{visible.length}</b> of <b>{rows.length}</b> installs</span>
                    {chips.map((c, i) => (
                      <span className="inst-fchip" data-fchip key={c.key}>
                        {c.text}
                        <button type="button" aria-label={`Remove ${c.text}`} onClick={() => { patchFilters(c.remove); setFocusChip(i); }}>×</button>
                      </span>
                    ))}
                    {anyApplied && <button type="button" className="inst-link" onClick={() => setFilters({ ...DEFAULT_FILTERS })}>Show all</button>}
                  </div>
                  <div className="inst-showing-right">
                    <input className="inst-input" type="search" placeholder="Find id, model, country…" aria-label="Find" value={filters.text} onChange={(e) => patchFilters({ text: e.target.value })} />
                    <button type="button" className="inst-btn" ref={filtersBtn} onClick={() => setDialog('filters')}>
                      Filters{dialogFilterCount(filters) > 0 && <span className="inst-badge">{dialogFilterCount(filters)}</span>}
                    </button>
                    <span className="inst-sortlabel">Sort by</span>
                    <select className="inst-select" aria-label="Sort by" value={sort.field} onChange={(e) => setSort((s) => ({ ...s, field: e.target.value }))}>
                      {SORT_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                    <button type="button" className="inst-btn" onClick={() => setSort((s) => ({ ...s, dir: s.dir === 'desc' ? 'asc' : 'desc' }))}>
                      {sort.dir === 'desc' ? 'Newest first ↓' : 'Oldest first ↑'}
                    </button>
                    <button type="button" className="inst-btn" onClick={exportCsv}>Export CSV</button>
                    <button type="button" className="inst-btn" onClick={() => copy(JSON.stringify({ meta, rows, orphans }, null, 2))}>Copy JSON</button>
                  </div>
                </div>

                <InstallsTable
                  rows={visible} firstHeader="First open" sort={sort} onSort={onSort} expanded={expanded}
                  onToggle={toggle} onFlag={(key, row) => setFlag({ key, row })} onCopy={copy}
                  showBuild={!bar.build} zone={zone} testId="inst-main-table"
                />

                {meta?.truncated && (
                  <p className="inst-trunc" role="note">
                    Stopped at the {meta.rowLimit} row cap — the earliest installs in the range are not shown, and the story
                    counts only the rows that are. Raise the row cap or shorten the range to see the rest.
                  </p>
                )}

                {orphans.length > 0 && (
                  <details className="inst-orphans" open>
                    <summary>{orphans.length} install event{orphans.length === 1 ? '' : 's'} with no first open — not counted above</summary>
                    <p className="inst-orphans-note">
                      pauser_app_install events in this range whose installation had no first_open in the two days before them.
                      In practice these are existing users on their first launch of a build that sends the event — the app counted
                      them as new installs. Answers are measured from the install event. Open a row for its os_install_time, which
                      shows how long ago it really installed.
                    </p>
                    <InstallsTable
                      rows={sortedOrphans} firstHeader="Install event" sort={sort} onSort={onSort} expanded={expanded}
                      onToggle={toggle} onFlag={(key, row) => setFlag({ key, row })} onCopy={copy}
                      showBuild={!bar.build} zone={zone} testId="inst-orphan-table"
                    />
                  </details>
                )}
              </>
            )}

            {toast && <div role="status" style={{ position: 'fixed', bottom: 20, right: 20, background: '#14282b', color: '#fff', padding: '6px 12px', borderRadius: 6, fontSize: 12.5, zIndex: 1100 }}>{toast}</div>}
          </div>
        </div>
      </div>

      {dialog === 'search' && (
        <SearchDialog
          search={shownSearch || search || defaultSearch()}
          versionsSeen={versionsSeen}
          now={now()}
          busy={phase === 'loading'}
          maxDays={cfg.maxDays}
          hint={cfg.hint}
          onSubmit={submitSearch}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'filters' && <FiltersDialog filters={filters} counts={counts} onChange={patchFilters} onClose={() => setDialog(null)} />}
      {dialog === 'events' && <EventsDialog onClose={() => setDialog(null)} onCopy={copy} />}
      {dialog === 'about' && <AboutDialog source={source} onClose={() => setDialog(null)} />}
      {flag && <FlagDialog flagKey={flag.key} row={flag.row} onClose={() => setFlag(null)} />}
    </div>
  );
};

export default Installs;
